// React Hooks for Task Templates
// Data fetching and mutations for task templates

import { useState, useEffect } from 'react';
import { taskTemplateService } from '../services/taskTemplateService';

/**
 * Hook to fetch all task templates with optional filters
 */
export function useTaskTemplates(filters = {}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await taskTemplateService.getAllTemplates(filters);
      setTemplates(data);
    } catch (err) {
      console.error('Error loading task templates:', err);
      setError(err.message);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [JSON.stringify(filters)]); // Re-fetch when filters change

  const refresh = () => {
    loadTemplates();
  };

  return { templates, loading, error, refresh };
}

/**
 * Hook to fetch a single task template by ID
 */
export function useTaskTemplate(templateId) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTemplate = async () => {
    if (!templateId) {
      setTemplate(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await taskTemplateService.getTemplate(templateId);
      setTemplate(data);
    } catch (err) {
      console.error('Error loading task template:', err);
      setError(err.message);
      setTemplate(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplate();
  }, [templateId]);

  const refresh = () => {
    loadTemplate();
  };

  return { template, loading, error, refresh };
}

/**
 * Hook to fetch templates by task type
 */
export function useTaskTemplatesByType(taskType) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTemplates = async () => {
    if (!taskType) {
      setTemplates([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await taskTemplateService.getTemplatesByType(taskType);
      setTemplates(data);
    } catch (err) {
      console.error('Error loading templates by type:', err);
      setError(err.message);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [taskType]);

  const refresh = () => {
    loadTemplates();
  };

  return { templates, loading, error, refresh };
}

/**
 * Hook for task template mutations (create, update, delete)
 */
export function useTaskTemplateMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createTemplate = async (templateData, userId) => {
    try {
      setLoading(true);
      setError(null);
      const templateId = await taskTemplateService.createTemplate(templateData, userId);
      return templateId;
    } catch (err) {
      console.error('Error creating template:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTemplate = async (templateId, updates, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskTemplateService.updateTemplate(templateId, updates, userId);
    } catch (err) {
      console.error('Error updating template:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (templateId, userId) => {
    try {
      setLoading(true);
      setError(null);
      await taskTemplateService.deleteTemplate(templateId, userId);
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const duplicateTemplate = async (templateId, userId) => {
    try {
      setLoading(true);
      setError(null);
      const newTemplateId = await taskTemplateService.duplicateTemplate(templateId, userId);
      return newTemplateId;
    } catch (err) {
      console.error('Error duplicating template:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const generateTask = async (templateId, customContext, assignedTo, scheduledStart, userId) => {
    try {
      setLoading(true);
      setError(null);
      const taskData = await taskTemplateService.generateTaskFromTemplate(
        templateId,
        customContext,
        assignedTo,
        scheduledStart,
        userId
      );
      return taskData;
    } catch (err) {
      console.error('Error generating task from template:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    generateTask,
    loading,
    error
  };
}

/**
 * Hook to fetch template statistics
 */
export function useTaskTemplateStats(templateId) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStats = async () => {
    if (!templateId) {
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await taskTemplateService.getTemplateStats(templateId);
      setStats(data);
    } catch (err) {
      console.error('Error loading template stats:', err);
      setError(err.message);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [templateId]);

  const refresh = () => {
    loadStats();
  };

  return { stats, loading, error, refresh };
}

/**
 * Hook to fetch most used templates
 */
export function useMostUsedTemplates(limit = 10) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await taskTemplateService.getMostUsedTemplates(limit);
      setTemplates(data);
    } catch (err) {
      console.error('Error loading most used templates:', err);
      setError(err.message);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [limit]);

  const refresh = () => {
    loadTemplates();
  };

  return { templates, loading, error, refresh };
}


